/* ── Status Effect Overlay ──────────────────────────────────────────
 * Renders per-drone status overlays driven by the schema fields synced
 * once per server tick by `SimulationLoop`:
 *   - shielded → cyan additive halo sphere, pulsing scale
 *   - taunted  → red exclamation-mark sprite billboarded above the drone
 *   - slowed   → blue chromatic-aberration vignette ring around the drone
 *   - invulnerableMs > 0 → spawn-protection countdown:
 *     · cyan-white aura halo, breathing pulse, intensity fades with timer
 *     · shrinking arc ring around the drone (360° → 0° over duration)
 *   - stunnedRemainingMs > 0 → flickering yellow ring around the drone
 *   - dotUntilMs > 0 → red ember pulse mesh growing/shrinking while DoT ticks
 *   - knockbackUntilMs > 0 → thin elongated streak trailing the drone in the
 *     velocity (last-position-delta) direction; fades within ~150ms
 *
 * Public API mirrors the project renderer convention:
 *   - constructor adds to scene
 *   - update(drones, deltaMs) animates
 *   - dispose() cleans up
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';
import {
  MAX_DRONES_PER_PLAYER,
  MAX_PLAYERS_PER_ROOM,
  SPAWN_INVULN_MS,
} from '../../shared/constants';

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/**
 * Minimal status-effect view of a drone — only the fields the overlay needs.
 * Matches `InterpolatedDrone` so callers can pass the same array used by
 * `DroneRenderer.update()`.
 */
export interface StatusDrone {
  id: string;
  alive: boolean;
  position: Vec3;
  shielded: boolean;
  taunted: boolean;
  slowed: boolean;
  invulnerableMs: number;
  stunnedRemainingMs?: number;
  dotUntilMs?: number;
  dotDps?: number;
  knockbackUntilMs?: number;
}

interface OverlaySlot {
  obj: THREE.Object3D;
  inUse: boolean;
}

const MAX_OVERLAY_SLOTS = MAX_DRONES_PER_PLAYER * MAX_PLAYERS_PER_ROOM;

const SHIELD_COLOR = 0x4ddcff;
const TAUNT_COLOR = 0xff3344;
const SLOW_COLOR = 0x4d6bff;
const INVULN_COLOR = 0xffffff;
const INVULN_RING_COLOR = 0xa8f5ff; // cyan-white, distinct from shield/leader rings
const STUN_COLOR = 0xffd84a;
const DOT_COLOR = 0xff4422;
const KNOCKBACK_COLOR = 0xfff0c0;

const SHIELD_BASE_SCALE = 1.6;
const SHIELD_PULSE_AMP = 0.18;
const SHIELD_PULSE_SPEED = 4.5;

const TAUNT_HEIGHT = 2.6;
const TAUNT_BOB_AMP = 0.12;
const TAUNT_BOB_SPEED = 6.0;

const SLOW_RING_RADIUS = 1.5;
const SLOW_RING_TUBE = 0.16;
const SLOW_PULSE_SPEED = 2.0;

const INVULN_PULSE_SPEED = 6.28; // ~1Hz breathing (2π rad/s)
const INVULN_RING_INNER = 1.55;
const INVULN_RING_OUTER = 1.85;
const INVULN_RING_SEGMENTS = 64;

const STUN_RING_RADIUS = 1.4;
const STUN_RING_TUBE = 0.10;
const STUN_FLICKER_SPEED = 22.0; // rapid flicker — distinct from slow's slow pulse

const DOT_PULSE_SPEED = 5.5;
const DOT_BASE_SCALE = 1.05;
const DOT_PULSE_AMP = 0.35;

const KNOCKBACK_FADE_MS = 150;
const KNOCKBACK_LENGTH = 1.6;
const KNOCKBACK_THICKNESS = 0.18;

function makeShieldHalo(): THREE.Mesh {
  const geom = new THREE.SphereGeometry(1, 16, 12);
  const mat = new THREE.MeshBasicMaterial({
    color: SHIELD_COLOR,
    transparent: true,
    opacity: 0.35,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.renderOrder = 5;
  mesh.frustumCulled = false;
  mesh.visible = false;
  return mesh;
}

function makeTauntMarker(): THREE.Group {
  // Stylized exclamation mark — a thin tall stem with a small dot below,
  // both red, yaw-billboarded each frame in update() so they face the camera.
  const group = new THREE.Group();
  group.frustumCulled = false;
  group.visible = false;
  const mat = new THREE.MeshBasicMaterial({
    color: TAUNT_COLOR,
    transparent: true,
    opacity: 0.95,
    depthWrite: false,
  });
  const stem = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.5, 0.04), mat);
  stem.position.y = 0.18;
  const dot = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.12, 0.04), mat);
  dot.position.y = -0.20;
  group.add(stem, dot);
  return group;
}

function makeSlowRing(): THREE.Mesh {
  const geom = new THREE.TorusGeometry(SLOW_RING_RADIUS, SLOW_RING_TUBE, 8, 24);
  const mat = new THREE.MeshBasicMaterial({
    color: SLOW_COLOR,
    transparent: true,
    opacity: 0.55,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.rotation.x = Math.PI / 2;
  mesh.renderOrder = 4;
  mesh.frustumCulled = false;
  mesh.visible = false;
  return mesh;
}

function makeInvulnHalo(): THREE.Mesh {
  const geom = new THREE.SphereGeometry(1, 12, 10);
  const mat = new THREE.MeshBasicMaterial({
    color: INVULN_COLOR,
    transparent: true,
    opacity: 0.25,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.renderOrder = 5;
  mesh.frustumCulled = false;
  mesh.visible = false;
  return mesh;
}

function makeInvulnRing(): THREE.Mesh {
  // Full circle ring; we crop the visible arc by mutating geometry.drawRange
  // so only the first N segments render — yielding a shrinking arc as the
  // spawn-protection timer counts down.
  const geom = new THREE.RingGeometry(
    INVULN_RING_INNER,
    INVULN_RING_OUTER,
    INVULN_RING_SEGMENTS,
    1,
  );
  const mat = new THREE.MeshBasicMaterial({
    color: INVULN_RING_COLOR,
    transparent: true,
    opacity: 0.85,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.rotation.x = Math.PI / 2; // lie flat (XZ plane)
  mesh.renderOrder = 6;
  mesh.frustumCulled = false;
  mesh.visible = false;
  return mesh;
}

function makeStunRing(): THREE.Mesh {
  // Yellow torus reusing the slow-ring geometry profile but rotated upright
  // around the drone. Flickering opacity is driven each frame in applyStun().
  const geom = new THREE.TorusGeometry(STUN_RING_RADIUS, STUN_RING_TUBE, 8, 24);
  const mat = new THREE.MeshBasicMaterial({
    color: STUN_COLOR,
    transparent: true,
    opacity: 0.9,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.rotation.x = Math.PI / 2;
  mesh.renderOrder = 5;
  mesh.frustumCulled = false;
  mesh.visible = false;
  return mesh;
}

function makeDotEmber(): THREE.Mesh {
  // Small icosahedron used as a particle-like ember pulse — cheaper than a
  // particle system for the single-pulse use case.
  const geom = new THREE.IcosahedronGeometry(0.35, 0);
  const mat = new THREE.MeshBasicMaterial({
    color: DOT_COLOR,
    transparent: true,
    opacity: 0.65,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.renderOrder = 5;
  mesh.frustumCulled = false;
  mesh.visible = false;
  return mesh;
}

function makeKnockbackStreak(): THREE.Mesh {
  // Thin elongated box stretched along the +X axis, oriented at runtime by
  // setting `mesh.rotation.y` to atan2(dirX, dirZ). The streak is anchored
  // behind the drone's current position via `mesh.position`.
  const geom = new THREE.BoxGeometry(KNOCKBACK_LENGTH, KNOCKBACK_THICKNESS, KNOCKBACK_THICKNESS);
  const mat = new THREE.MeshBasicMaterial({
    color: KNOCKBACK_COLOR,
    transparent: true,
    opacity: 0.85,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.renderOrder = 5;
  mesh.frustumCulled = false;
  mesh.visible = false;
  return mesh;
}

interface EffectPool {
  pool: OverlaySlot[];
  byDrone: Map<string, number>;
  apply: (slot: OverlaySlot, pos: Vec3, payloadValue?: number, droneId?: string) => void;
}

// Remaining-time fraction for spawn-protection effects (1 at start, 0 at end).
function invulnFraction(ms: number | undefined): number {
  const remaining = Math.max(0, Math.min(SPAWN_INVULN_MS, ms ?? SPAWN_INVULN_MS));
  return remaining / SPAWN_INVULN_MS;
}

export class StatusEffectOverlay {
  private group = new THREE.Group();
  private elapsed = 0;
  private camera: THREE.Camera | null = null;

  private shield: EffectPool;
  private taunt: EffectPool;
  private slow: EffectPool;
  private invuln: EffectPool;
  private invulnRing: EffectPool;
  private stun: EffectPool;
  private dot: EffectPool;
  private knockback: EffectPool;

  // Last-known position per drone — used to derive a knockback streak
  // direction from frame-to-frame motion since velocity is not part of the
  // InterpolatedDrone projection.
  private lastPositions: Map<string, Vec3> = new Map();

  // Per-update scratch buffers — cleared at the top of update() rather than
  // re-allocated, so the 144Hz render loop avoids ~10 fresh collections per
  // frame and the per-drone Vec3 cache below is mutated in place.
  private readonly _needsShield = new Set<string>();
  private readonly _needsTaunt = new Set<string>();
  private readonly _needsSlow = new Set<string>();
  private readonly _needsInvuln = new Set<string>();
  private readonly _needsStun = new Set<string>();
  private readonly _needsDot = new Set<string>();
  private readonly _needsKnockback = new Set<string>();
  private readonly _invulnMs = new Map<string, number>();
  private readonly _dotDps = new Map<string, number>();
  private readonly _knockbackAge = new Map<string, number>();
  private readonly _positions = new Map<string, Vec3>();
  private readonly _aliveIds = new Set<string>();
  // Time since each drone's knockback first entered the active state, so the
  // streak fades smoothly within KNOCKBACK_FADE_MS regardless of the
  // remaining server timer.
  private knockbackElapsedMs: Map<string, number> = new Map();

  constructor(parent: THREE.Object3D) {
    this.group.name = 'StatusEffectOverlay';
    parent.add(this.group);

    this.shield = this.buildPool(makeShieldHalo, (slot, pos) => this.applyShield(slot, pos));
    this.taunt = this.buildPool(makeTauntMarker, (slot, pos) => this.applyTaunt(slot, pos));
    this.slow = this.buildPool(makeSlowRing, (slot, pos) => this.applySlow(slot, pos));
    this.invuln = this.buildPool(makeInvulnHalo, (slot, pos, ms) => this.applyInvuln(slot, pos, ms));
    this.invulnRing = this.buildPool(
      makeInvulnRing,
      (slot, pos, ms) => this.applyInvulnRing(slot, pos, ms),
    );
    this.stun = this.buildPool(makeStunRing, (slot, pos) => this.applyStun(slot, pos));
    this.dot = this.buildPool(makeDotEmber, (slot, pos, dps) => this.applyDot(slot, pos, dps));
    this.knockback = this.buildPool(
      makeKnockbackStreak,
      (slot, pos, age, id) => this.applyKnockback(slot, pos, age, id),
    );
  }

  private buildPool(
    factory: () => THREE.Object3D,
    apply: (slot: OverlaySlot, pos: Vec3, payloadValue?: number, droneId?: string) => void,
  ): EffectPool {
    const pool: OverlaySlot[] = [];
    for (let i = 0; i < MAX_OVERLAY_SLOTS; i++) {
      const obj = factory();
      this.group.add(obj);
      pool.push({ obj, inUse: false });
    }
    return { pool, byDrone: new Map(), apply };
  }

  /**
   * Optional: tell the overlay which camera the taunt markers should face.
   * If unset, the markers stay axis-aligned (still readable, just less
   * theatrical). Cheap to set every frame.
   */
  setCamera(camera: THREE.Camera): void {
    this.camera = camera;
  }

  update(drones: ReadonlyArray<StatusDrone>, deltaMs: number): void {
    const dt = Math.max(deltaMs, 0);
    this.elapsed += dt / 1000;

    const needsShield = this._needsShield; needsShield.clear();
    const needsTaunt = this._needsTaunt; needsTaunt.clear();
    const needsSlow = this._needsSlow; needsSlow.clear();
    const needsInvuln = this._needsInvuln; needsInvuln.clear();
    const needsStun = this._needsStun; needsStun.clear();
    const needsDot = this._needsDot; needsDot.clear();
    const needsKnockback = this._needsKnockback; needsKnockback.clear();
    const invulnMs = this._invulnMs; invulnMs.clear();
    const dotDps = this._dotDps; dotDps.clear();
    const knockbackAge = this._knockbackAge; knockbackAge.clear();
    const positions = this._positions; positions.clear();
    const aliveIds = this._aliveIds; aliveIds.clear();

    for (const d of drones) {
      if (!d.alive) continue;
      aliveIds.add(d.id);
      positions.set(d.id, d.position);
      if (d.shielded) needsShield.add(d.id);
      if (d.taunted) needsTaunt.add(d.id);
      if (d.slowed) needsSlow.add(d.id);
      if (d.invulnerableMs > 0) {
        needsInvuln.add(d.id);
        invulnMs.set(d.id, d.invulnerableMs);
      }
      if ((d.stunnedRemainingMs ?? 0) > 0) needsStun.add(d.id);
      if ((d.dotUntilMs ?? 0) > 0) {
        needsDot.add(d.id);
        dotDps.set(d.id, d.dotDps ?? 0);
      }
      if ((d.knockbackUntilMs ?? 0) > 0) {
        const age = (this.knockbackElapsedMs.get(d.id) ?? 0) + dt;
        if (age <= KNOCKBACK_FADE_MS) {
          this.knockbackElapsedMs.set(d.id, age);
          needsKnockback.add(d.id);
          knockbackAge.set(d.id, age);
        } else {
          // Streak window elapsed — keep counting until server clears the
          // timer, but stop rendering the trail.
          this.knockbackElapsedMs.set(d.id, age);
        }
      } else {
        this.knockbackElapsedMs.delete(d.id);
      }
    }

    // Drop knockback timers for drones that have left the snapshot entirely.
    for (const id of this.knockbackElapsedMs.keys()) {
      if (!aliveIds.has(id)) this.knockbackElapsedMs.delete(id);
    }

    this.syncPool(this.shield, needsShield, positions);
    this.syncPool(this.taunt, needsTaunt, positions);
    this.syncPool(this.slow, needsSlow, positions);
    this.syncPool(this.invuln, needsInvuln, positions, invulnMs);
    this.syncPool(this.invulnRing, needsInvuln, positions, invulnMs);
    this.syncPool(this.stun, needsStun, positions);
    this.syncPool(this.dot, needsDot, positions, dotDps);
    this.syncPool(this.knockback, needsKnockback, positions, knockbackAge);

    // Update last-known positions AFTER applies so the knockback streak
    // direction reflects the prior frame's heading (delta = current - prev),
    // and prune entries for drones no longer alive. Reuse cached Vec3
    // objects so the per-frame render loop doesn't churn GC.
    for (const id of this.lastPositions.keys()) {
      if (!aliveIds.has(id)) this.lastPositions.delete(id);
    }
    for (const [id, pos] of positions) {
      const cached = this.lastPositions.get(id);
      if (cached) {
        cached.x = pos.x; cached.y = pos.y; cached.z = pos.z;
      } else {
        this.lastPositions.set(id, { x: pos.x, y: pos.y, z: pos.z });
      }
    }
  }

  private syncPool(
    effect: EffectPool,
    needs: ReadonlySet<string>,
    positions: ReadonlyMap<string, Vec3>,
    payload?: ReadonlyMap<string, number>,
  ): void {
    const { pool, byDrone, apply } = effect;

    // Release stale assignments.
    for (const [id, idx] of byDrone) {
      if (needs.has(id)) continue;
      const slot = pool[idx];
      if (slot) {
        slot.inUse = false;
        slot.obj.visible = false;
      }
      byDrone.delete(id);
    }

    // Acquire/refresh assignments.
    for (const id of needs) {
      let idx = byDrone.get(id);
      if (idx === undefined) {
        idx = pool.findIndex((s) => !s.inUse);
        if (idx < 0) continue; // pool exhausted — silently skip
        pool[idx].inUse = true;
        byDrone.set(id, idx);
      }
      const pos = positions.get(id);
      if (pos) apply(pool[idx], pos, payload?.get(id), id);
    }
  }

  private applyShield(slot: OverlaySlot, pos: Vec3): void {
    const mesh = slot.obj;
    mesh.visible = true;
    const wave = (Math.sin(this.elapsed * SHIELD_PULSE_SPEED) + 1) * 0.5;
    const scale = SHIELD_BASE_SCALE * (1 + wave * SHIELD_PULSE_AMP);
    mesh.position.set(pos.x, pos.y, pos.z);
    mesh.scale.setScalar(scale);
  }

  private applyTaunt(slot: OverlaySlot, pos: Vec3): void {
    const obj = slot.obj;
    obj.visible = true;
    const bob = Math.sin(this.elapsed * TAUNT_BOB_SPEED) * TAUNT_BOB_AMP;
    obj.position.set(pos.x, pos.y + TAUNT_HEIGHT + bob, pos.z);
    if (this.camera) {
      const dx = this.camera.position.x - pos.x;
      const dz = this.camera.position.z - pos.z;
      obj.rotation.set(0, Math.atan2(dx, dz), 0);
    }
  }

  private applySlow(slot: OverlaySlot, pos: Vec3): void {
    const mesh = slot.obj as THREE.Mesh;
    mesh.visible = true;
    const wave = (Math.sin(this.elapsed * SLOW_PULSE_SPEED) + 1) * 0.5;
    mesh.position.set(pos.x, pos.y - 0.4, pos.z);
    mesh.rotation.z = this.elapsed * 0.6;
    (mesh.material as THREE.MeshBasicMaterial).opacity = 0.35 + wave * 0.4;
  }

  private applyInvuln(slot: OverlaySlot, pos: Vec3, ms?: number): void {
    const mesh = slot.obj as THREE.Mesh;
    mesh.visible = true;
    // Aura intensity scales with timer so it fades to nothing as the
    // countdown ends — keeps the pulse and the arc ring in lockstep.
    const t = invulnFraction(ms);
    const wave = (Math.sin(this.elapsed * INVULN_PULSE_SPEED) + 1) * 0.5;
    mesh.position.set(pos.x, pos.y, pos.z);
    mesh.scale.setScalar(1.9 + wave * 0.25 * t);
    (mesh.material as THREE.MeshBasicMaterial).opacity = (0.18 + wave * 0.18) * t;
  }

  private applyInvulnRing(slot: OverlaySlot, pos: Vec3, ms?: number): void {
    const mesh = slot.obj as THREE.Mesh;
    mesh.position.set(pos.x, pos.y, pos.z);
    // RingGeometry indices are laid out segment-by-segment, 6 indices per
    // segment (two triangles bridging inner→outer). Truncating drawRange
    // crops the visible arc cleanly without reallocating the geometry.
    const visibleSegments = Math.max(0, Math.round(invulnFraction(ms) * INVULN_RING_SEGMENTS));
    const indexCount = visibleSegments * 6;
    mesh.geometry.setDrawRange(0, indexCount);
    mesh.visible = indexCount > 0;
  }

  private applyStun(slot: OverlaySlot, pos: Vec3): void {
    const mesh = slot.obj as THREE.Mesh;
    mesh.visible = true;
    mesh.position.set(pos.x, pos.y + 0.6, pos.z);
    mesh.rotation.z = this.elapsed * 1.4;
    // Two-tone flicker: a fast sine plus a noisy step keeps the ring lively
    // without disappearing entirely on any given frame.
    const flicker = (Math.sin(this.elapsed * STUN_FLICKER_SPEED) + 1) * 0.5;
    const step = Math.floor(this.elapsed * 18) % 2 === 0 ? 1.0 : 0.55;
    (mesh.material as THREE.MeshBasicMaterial).opacity = (0.45 + flicker * 0.5) * step;
  }

  private applyDot(slot: OverlaySlot, pos: Vec3, dps?: number): void {
    const mesh = slot.obj as THREE.Mesh;
    mesh.visible = true;
    // Pulse grows/shrinks repeatedly while the timer ticks. Higher DPS makes
    // the ember larger so heavy burns read as heavier visually.
    const wave = (Math.sin(this.elapsed * DOT_PULSE_SPEED) + 1) * 0.5;
    const intensity = 1 + Math.min(1, (dps ?? 0) / 30); // cap so visuals don't blow up
    const scale = DOT_BASE_SCALE * (1 + wave * DOT_PULSE_AMP) * intensity;
    mesh.position.set(pos.x, pos.y + 0.2 + wave * 0.3, pos.z);
    mesh.scale.setScalar(scale);
    (mesh.material as THREE.MeshBasicMaterial).opacity = 0.55 + wave * 0.35;
  }

  private applyKnockback(slot: OverlaySlot, pos: Vec3, ageMs?: number, droneId?: string): void {
    const mesh = slot.obj as THREE.Mesh;
    const age = ageMs ?? 0;
    const t = Math.max(0, Math.min(1, 1 - age / KNOCKBACK_FADE_MS));
    if (t <= 0) {
      mesh.visible = false;
      return;
    }
    mesh.visible = true;
    // Direction = current - previous frame position. If unknown (first
    // frame), aim along +X so the streak still renders rather than
    // collapsing to a zero-length sliver.
    const prev = droneId ? this.lastPositions.get(droneId) : undefined;
    let dx = 1;
    let dz = 0;
    if (prev) {
      dx = pos.x - prev.x;
      dz = pos.z - prev.z;
    }
    const len = Math.hypot(dx, dz);
    if (len > 1e-4) {
      dx /= len;
      dz /= len;
    } else {
      dx = 1;
      dz = 0;
    }
    // Anchor the streak slightly behind the drone in the direction it is
    // travelling so it reads as a trail rather than a forward spike.
    const offset = KNOCKBACK_LENGTH * 0.5;
    mesh.position.set(pos.x - dx * offset, pos.y, pos.z - dz * offset);
    mesh.rotation.set(0, Math.atan2(dx, dz) - Math.PI / 2, 0);
    (mesh.material as THREE.MeshBasicMaterial).opacity = 0.85 * t;
    mesh.scale.set(t, 1, 1);
  }

  /** Test/inspection helpers — number of pool slots currently in use. */
  getActiveShieldCount(): number { return this.shield.byDrone.size; }
  getActiveTauntCount(): number { return this.taunt.byDrone.size; }
  getActiveSlowCount(): number { return this.slow.byDrone.size; }
  getActiveInvulnCount(): number { return this.invuln.byDrone.size; }
  getActiveStunRingCount(): number { return this.stun.byDrone.size; }
  getActiveDotPulseCount(): number { return this.dot.byDrone.size; }
  getActiveKnockbackStreakCount(): number { return this.knockback.byDrone.size; }
  getActiveInvulnRingCount(): number {
    // Count only ring slots whose draw range is non-zero — when ms hits 0
    // the slot is hidden but the byDrone entry survives until the next
    // frame's release pass.
    let n = 0;
    for (const idx of this.invulnRing.byDrone.values()) {
      const mesh = this.invulnRing.pool[idx]?.obj as THREE.Mesh | undefined;
      if (mesh && mesh.visible) n++;
    }
    return n;
  }

  /**
   * Test helper — fraction of the arc currently drawn for the given drone
   * (0..1), or null if the drone has no active invuln ring slot.
   */
  getInvulnRingFraction(droneId: string): number | null {
    const idx = this.invulnRing.byDrone.get(droneId);
    if (idx === undefined) return null;
    const mesh = this.invulnRing.pool[idx]?.obj as THREE.Mesh | undefined;
    if (!mesh) return null;
    const drawRange = mesh.geometry.drawRange;
    return drawRange.count / (INVULN_RING_SEGMENTS * 6);
  }

  /** Test helper — current aura opacity for the given drone (or null). */
  getInvulnAuraOpacity(droneId: string): number | null {
    const idx = this.invuln.byDrone.get(droneId);
    if (idx === undefined) return null;
    const mesh = this.invuln.pool[idx]?.obj as THREE.Mesh | undefined;
    if (!mesh) return null;
    return (mesh.material as THREE.MeshBasicMaterial).opacity;
  }

  dispose(): void {
    // Taunt markers share a single material across stem+dot; dedupe so we
    // dispose each unique resource exactly once.
    const seenMaterials = new Set<THREE.Material>();
    const seenGeometries = new Set<THREE.BufferGeometry>();
    this.group.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (mesh.geometry && !seenGeometries.has(mesh.geometry)) {
        seenGeometries.add(mesh.geometry);
        mesh.geometry.dispose();
      }
      const mat = mesh.material;
      if (Array.isArray(mat)) {
        for (const m of mat) {
          if (!seenMaterials.has(m)) { seenMaterials.add(m); m.dispose(); }
        }
      } else if (mat) {
        const m = mat as THREE.Material;
        if (!seenMaterials.has(m)) { seenMaterials.add(m); m.dispose(); }
      }
    });
    if (this.group.parent) this.group.parent.remove(this.group);
    this.shield.byDrone.clear();
    this.taunt.byDrone.clear();
    this.slow.byDrone.clear();
    this.invuln.byDrone.clear();
    this.invulnRing.byDrone.clear();
    this.stun.byDrone.clear();
    this.dot.byDrone.clear();
    this.knockback.byDrone.clear();
    this.lastPositions.clear();
    this.knockbackElapsedMs.clear();
  }
}
